// src/components/pages/SupportPage/SupportChatPage.tsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {useNavigate} from 'react-router-dom';
import { useUserData } from '../../services/useUserData';
import Loader from '../../widgets/Loader/Loader';
import BackgroundCanvasWithoutRadar from '../../widgets/canvas/BackgroundCanvasWithoutRadar';
import { SupportIcons } from '../../assets/images/supportIcons';
import {BackIcon} from '../../assets/images/BackIcon';

import {
    buildAttachmentUrl,
    getSupportMessages,
    markSupportAsRead,
    sendSupportMessageWithFiles,
    sendSupportTextMessage,
    SupportAttachment,
    SupportMessage,
} from '../../services/supportApi';

import './SupportChatPage.scss';

type ChatFile = {
    id: number;
    name: string;
    size: number;
    type: string;
    file: File;
};

type GroupedMessage = {
    id: string;
    sender_type: 'user' | 'admin' | 'system';
    text: string | null;
    attachments: SupportAttachment[];
    created_at: string | null;
    sourceIds: number[];
};

interface SupportChatPageProps {
    onModalStateChange?: (isOpen: boolean) => void;
}

const GROUP_TIME_DIFF_MS = 4000;

function parseDateSafe(value: string | null): number | null {
    if (!value) return null;
    const time = new Date(value).getTime();
    return Number.isNaN(time) ? null : time;
}

function hasText(message: SupportMessage): boolean {
    return Boolean(message.text?.trim());
}

function hasAttachments(message: SupportMessage): boolean {
    return Boolean(message.attachments?.length);
}

function shouldMergeMessages(prev: SupportMessage, next: SupportMessage): boolean {
    if (prev.sender_type !== next.sender_type) return false;
    if (prev.reply_to_message_id || next.reply_to_message_id) return false;

    const prevTime = parseDateSafe(prev.created_at);
    const nextTime = parseDateSafe(next.created_at);

    if (prevTime === null || nextTime === null) return false;
    if (Math.abs(nextTime - prevTime) > GROUP_TIME_DIFF_MS) return false;

    const prevHasText = hasText(prev);
    const nextHasText = hasText(next);
    const prevHasAttachments = hasAttachments(prev);
    const nextHasAttachments = hasAttachments(next);

    // Не объединяем два обычных текстовых сообщения
    if (prevHasText && nextHasText && !prevHasAttachments && !nextHasAttachments) {
        return false;
    }

    // Не объединяем два отдельных файловых сообщения
    if (!prevHasText && !nextHasText && prevHasAttachments && nextHasAttachments) {
        return false;
    }

    // Объединяем только связку "файл + текст" / "текст + файл"
    const isFileTextPair =
        (prevHasAttachments && !prevHasText && nextHasText && !nextHasAttachments) ||
        (prevHasText && !prevHasAttachments && nextHasAttachments && !nextHasText) ||
        (prevHasAttachments && nextHasAttachments && (prevHasText || nextHasText));

    return isFileTextPair;
}

function groupMessages(messages: SupportMessage[]): GroupedMessage[] {
    if (!messages.length) return [];

    const result: GroupedMessage[] = [];
    let currentGroup: GroupedMessage | null = null;
    let prevRaw: SupportMessage | null = null;

    for (const message of messages) {
        if (!currentGroup || !prevRaw || !shouldMergeMessages(prevRaw, message)) {
            currentGroup = {
                id: `group-${message.id}`,
                sender_type: message.sender_type,
                text: message.text?.trim() || null,
                attachments: [...(message.attachments || [])],
                created_at: message.created_at,
                sourceIds: [message.id],
            };

            result.push(currentGroup);
            prevRaw = message;
            continue;
        }

        if (message.attachments?.length) {
            currentGroup.attachments.push(...message.attachments);
        }

        const nextText = message.text?.trim();
        if (nextText) {
            currentGroup.text = currentGroup.text
                ? `${currentGroup.text}\n${nextText}`
                : nextText;
        }

        currentGroup.created_at = message.created_at || currentGroup.created_at;
        currentGroup.sourceIds.push(message.id);
        prevRaw = message;
    }

    return result;
}

function SupportPage({onModalStateChange}: SupportChatPageProps) {
    const navigate = useNavigate();
    const { loading, userData } = useUserData();
    const [isMobile, setIsMobile] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isSending, setIsSending] = useState(false);
    const [inputValue, setInputValue] = useState('');
    const [attachedFiles, setAttachedFiles] = useState<ChatFile[]>([]);
    const [rawMessages, setRawMessages] = useState<SupportMessage[]>([]);
    const [previewImage, setPreviewImage] = useState<{
        url: string;
        name: string;
        downloadUrl: string;
    } | null>(null);

    const [isAtBottom, setIsAtBottom] = useState(true);
    const [unseenCount, setUnseenCount] = useState(0);

    const messagesEndRef = useRef<HTMLDivElement | null>(null);
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const chatBodyRef = useRef<HTMLDivElement | null>(null);
    const firstLoadDoneRef = useRef(false);
    const messagesRequestRef = useRef(false);

    const userId = String(userData?.telegram_id || '');
    const groupedMessages = useMemo(() => groupMessages(rawMessages), [rawMessages]);
    const markAsReadOnServer = async () => {
        if (!userId) return;

            try {
                await markSupportAsRead(userId);
                setUnseenCount(0);
            } catch (error) {
            }
        };
    useEffect(() => {
        const checkMobile = () => {
            const mobile = window.innerWidth <= 1023;
            setIsMobile(mobile);

            // Управляем тулбаром только на мобильных
            if (onModalStateChange) {
                onModalStateChange(mobile);
            }
        };

        checkMobile();
        window.addEventListener('resize', checkMobile);

        return () => {
            window.removeEventListener('resize', checkMobile);
            // Возвращаем тулбар при размонтировании
            if (onModalStateChange) {
                onModalStateChange(false);
            }
        };
    }, [onModalStateChange]);

    useEffect(() => {
        const tg = (window as any).Telegram?.WebApp;
        if (tg) {
            tg.ready();
            tg.expand();
            tg.setHeaderColor?.('#000000');
            tg.setBackgroundColor?.('#000000');
        }
    }, []);

    useEffect(() => {
        if (!previewImage) return;

        const originalOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';

        return () => {
            document.body.style.overflow = originalOverflow;
        };
    }, [previewImage]);

    useEffect(() => {
        const handleEsc = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                setPreviewImage(null);
            }
        };

        window.addEventListener('keydown', handleEsc);
        return () => window.removeEventListener('keydown', handleEsc);
    }, []);

    const scrollToBottom = (behavior: ScrollBehavior = 'smooth') => {
        messagesEndRef.current?.scrollIntoView({ behavior });
    };

    const checkIsAtBottom = () => {
        const el = chatBodyRef.current;
        if (!el) return true;

        const threshold = 80;
        const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
        return distanceFromBottom <= threshold;
    };

    const handleChatScroll = () => {
        const atBottom = checkIsAtBottom();
        setIsAtBottom(atBottom);

        if (atBottom) {
            setUnseenCount(0);
            markAsReadOnServer();
        }
    };

    const loadMessages = async () => {
        if (!userId || messagesRequestRef.current) return;

        messagesRequestRef.current = true;

        try {
            const data = await getSupportMessages(userId);
            const nextRawMessages = data.messages || [];

            setRawMessages((prevRaw) => {
                const prevGrouped = groupMessages(prevRaw);
                const nextGrouped = groupMessages(nextRawMessages);

                if (firstLoadDoneRef.current && nextGrouped.length > prevGrouped.length) {
                    const addedGroups = nextGrouped.length - prevGrouped.length;

                    if (checkIsAtBottom()) {
                        setUnseenCount(0);
                    } else {
                        setUnseenCount((current) => current + addedGroups);
                    }
                }

                return nextRawMessages;
            });

            if (!firstLoadDoneRef.current) {
                firstLoadDoneRef.current = true;

                requestAnimationFrame(() => {
                    scrollToBottom('auto');
                });
            }
        } catch (error) {
        } finally {
            messagesRequestRef.current = false;
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (!userId) return;

        loadMessages().then(() => {
            markAsReadOnServer();
        });

        const interval = setInterval(() => {
            if (document.visibilityState === 'hidden') {
                return;
            }

            loadMessages();
        }, 5000);

        return () => clearInterval(interval);
    }, [userId]);

    const canSend = useMemo(() => {
        return (inputValue.trim().length > 0 || attachedFiles.length > 0) && !isSending;
    }, [inputValue, attachedFiles, isSending]);

    const formatFileSize = (bytes: number) => {
        if (bytes < 1024) return `${bytes} Б`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} КБ`;
        return `${(bytes / (1024 * 1024)).toFixed(1)} МБ`;
    };

    const handleOpenFilePicker = () => {
        fileInputRef.current?.click();
    };

    const handleFilesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;

        const mappedFiles: ChatFile[] = files.map((file, index) => ({
            id: Date.now() + index,
            name: file.name,
            size: file.size,
            type: file.type,
            file,
        }));

        setAttachedFiles((prev) => [...prev, ...mappedFiles]);
        e.target.value = '';
    };

    const handleRemoveFile = (fileId: number) => {
        setAttachedFiles((prev) => prev.filter((file) => file.id !== fileId));
    };

    const handleSend = async () => {
        if (!canSend || !userId) return;

        try {
            setIsSending(true);

            if (attachedFiles.length > 0) {
                await sendSupportMessageWithFiles(
                    userId,
                    inputValue,
                    attachedFiles.map((item) => item.file)
                );
            } else {
                await sendSupportTextMessage(userId, inputValue.trim());
            }

            setInputValue('');
            setAttachedFiles([]);
            await loadMessages();

            requestAnimationFrame(() => {
                scrollToBottom('smooth');
            });

            setUnseenCount(0);
        } catch (error) {
        } finally {
            setIsSending(false);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            handleSend();
        }
    };

    if (loading || isLoading) {
        return (
            <div className="tg-app">
                <BackgroundCanvasWithoutRadar />
                <div className="support-chat-page loading">
                    <div className="loader">
                        <Loader />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="tg-app">
            <BackgroundCanvasWithoutRadar />

            <div className="support-chat-page">
                <div className="support-chat-card">
                    <div className="support-chat-header">
                        <button className="back-button" onClick={() => navigate('/support')}>
                            <BackIcon/>
                        </button>
                        <div className="support-chat-header__icon">
                            <SupportIcons.support />
                        </div>

                        <div className="support-chat-header__content">
                            <div className="support-chat-header__title">Поддержка</div>
                            <div className="support-chat-header__subtitle">
                                Среднее время ответа: 2–4 часа
                            </div>
                        </div>

                    </div>

                    <div
                        ref={chatBodyRef}
                        className="support-chat-body"
                        onScroll={handleChatScroll}
                    >
                        {groupedMessages.map((message) => {
                            const hasText = Boolean(message.text?.trim());
                            const hasAttachments = Boolean(message.attachments?.length);

                            return (
                                <div
                                    key={message.id}
                                    className={`message-row ${message.sender_type === 'user' ? 'user' : 'support'}`}
                                >
                                    <div
                                        className={`telegram-bubble ${message.sender_type === 'user' ? 'user' : 'support'}`}
                                    >
                                        {hasAttachments && (
                                            <div className="telegram-bubble__attachments">
                                                {message.attachments.map((attachment) => {
                                                    const previewUrl = buildAttachmentUrl(attachment.view_url);
                                                    const downloadUrl = buildAttachmentUrl(
                                                        attachment.download_url || attachment.view_url
                                                    );

                                                    const mime = attachment.mime_type || '';
                                                    const fileName = (attachment.file_name || '').toLowerCase();

                                                    const isImage =
                                                        attachment.telegram_media_type === 'photo' ||
                                                        mime.startsWith('image/') ||
                                                        fileName.endsWith('.jpg') ||
                                                        fileName.endsWith('.jpeg') ||
                                                        fileName.endsWith('.png') ||
                                                        fileName.endsWith('.webp') ||
                                                        fileName.endsWith('.gif');

                                                    if (isImage) {
                                                        return (
                                                            <button
                                                                key={attachment.id}
                                                                type="button"
                                                                className="telegram-image"
                                                                onClick={() =>
                                                                    setPreviewImage({
                                                                        url: previewUrl,
                                                                        name: attachment.file_name || 'image',
                                                                        downloadUrl,
                                                                    })
                                                                }
                                                            >
                                                                <img
                                                                    src={previewUrl}
                                                                    alt={attachment.file_name || 'image'}
                                                                    className="telegram-image__img"
                                                                    loading="lazy"
                                                                />
                                                            </button>
                                                        );
                                                    }

                                                    return (
                                                        <a
                                                            key={attachment.id}
                                                            className="telegram-file"
                                                            href={downloadUrl}
                                                            download={attachment.file_name || 'file'}
                                                        >
                                                            <div className="telegram-file__icon">📄</div>
                                                            <div className="telegram-file__content">
                                                                <div className="telegram-file__name">
                                                                    {attachment.file_name || 'Файл'}
                                                                </div>
                                                                <div className="telegram-file__meta">
                                                                    {attachment.file_size
                                                                        ? formatFileSize(attachment.file_size)
                                                                        : ''}
                                                                </div>
                                                            </div>
                                                        </a>
                                                    );
                                                })}
                                            </div>
                                        )}

                                        {hasText && (
                                            <div className="telegram-bubble__text">
                                                {message.text}
                                            </div>
                                        )}

                                        <div className="telegram-bubble__footer">
                                            <span className="telegram-bubble__time">
                                                {message.created_at
                                                    ? new Date(message.created_at).toLocaleTimeString([], {
                                                          hour: '2-digit',
                                                          minute: '2-digit',
                                                      })
                                                    : ''}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}

                        <div ref={messagesEndRef} />
                    </div>

                    {!isAtBottom && (
                        <button
                            type="button"
                            className="scroll-to-bottom-button"
                            onClick={() => {
                                scrollToBottom('smooth');
                                setUnseenCount(0);
                            }}
                        >
                            <span className="scroll-to-bottom-button__arrow" aria-hidden="true">
                                <svg
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M6 9L12 15L18 9"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                            </span>

                            {unseenCount > 0 && (
                                <span className="scroll-to-bottom-button__badge">
                                    {Math.ceil(unseenCount / 2)}
                                </span>
                            )}
                        </button>
                    )}

                    {attachedFiles.length > 0 && (
                        <div className="attached-files-preview">
                            {attachedFiles.map((file) => (
                                <div key={file.id} className="attached-file-chip">
                                    <div className="attached-file-chip__info">
                                        <div className="attached-file-chip__name">{file.name}</div>
                                        <div className="attached-file-chip__size">
                                            {formatFileSize(file.size)}
                                        </div>
                                    </div>

                                    <button
                                        type="button"
                                        className="attached-file-chip__remove"
                                        onClick={() => handleRemoveFile(file.id)}
                                    >
                                        ×
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}

                    <div className="support-chat-input">
                        <input
                            ref={fileInputRef}
                            type="file"
                            multiple
                            className="hidden-file-input"
                            onChange={handleFilesChange}
                        />

                        <button
                            type="button"
                            className="attach-button"
                            onClick={handleOpenFilePicker}
                        >
                            +
                        </button>

                        <div className="support-chat-input__box">
                            <input
                                type="text"
                                placeholder="Введите сообщение..."
                                value={inputValue}
                                onChange={(e) => setInputValue(e.target.value)}
                                onKeyDown={handleKeyDown}
                            />
                        </div>

                        <button
                            className={`send-button ${canSend ? 'active' : ''}`}
                            onClick={handleSend}
                            disabled={!canSend}
                        >
                            <SupportIcons.chevronRight />
                        </button>
                    </div>
                </div>
            </div>

            {previewImage && (
                <div
                    className="image-preview-overlay"
                    onClick={() => setPreviewImage(null)}
                >
                    <div
                        className="image-preview-modal"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button
                            type="button"
                            className="image-preview-close"
                            onClick={() => setPreviewImage(null)}
                        >
                            ×
                        </button>

                        <div className="image-preview-wrapper">
                            <img
                                src={previewImage.url}
                                alt={previewImage.name}
                                className="image-preview-full"
                            />

                            <a
                                href={previewImage.downloadUrl}
                                download={previewImage.name}
                                className="image-preview-download"
                                aria-label="Скачать изображение"
                                title="Скачать"
                            >
                                <svg
                                    width="20"
                                    height="20"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M12 3V14"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                    <path
                                        d="M7 10L12 15L17 10"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                    <path
                                        d="M5 19H19"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                            </a>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default SupportPage;